import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Typography } from '@mui/material';
import { FC, Fragment, JSX, useCallback, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
// components
import { Alert } from 'components/common/Alert';
import ExcipientLabelForm from './ExcipientLabelForm';
import { BackdropLoader } from 'components/common/BackdropLoader';
// Schema, styles, graphql, constants
import { flexCenterBetween } from 'styles/commonComponentStyle';
import {
  ExcipientLabelPayload,
  useGetExcipientLabelLazyQuery,
  useUpdateExcipientLabelMutation,
} from 'generated/graphql';
import {
  HTTP_STATUS,
  EDIT_EXCIPIENT_LABEL_TEXT,
  excipientLabelInitialValue,
  EXCIPIENT_LABEL_IS_UPDATED_TEXT,
} from 'constants/index';
import { AddExcipientLabelFormType, ParamType } from 'interfaces';
import { updateExcipientLabelSchema } from 'validationSchema';

const Edit: FC = (): JSX.Element => {
  const navigation = useNavigate();
  const params = useParams<ParamType>();
  const { id } = params || {};

  const excipientLabelMethods = useForm<AddExcipientLabelFormType>({
    defaultValues: excipientLabelInitialValue,
    resolver: yupResolver(updateExcipientLabelSchema),
  });

  const { handleSubmit: handleExcipientLabelSubmit, setValue: setExcipientLabelValue } =
    excipientLabelMethods;

  const [updateExcipientLabel, { loading: ExcipientLoading }] = useUpdateExcipientLabelMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { updateExcipientLabel } = data;
      const { response } = updateExcipientLabel || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        navigation(-1);
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const [getExcipientLabel, { loading: getLoading }] = useGetExcipientLabelLazyQuery({
    onCompleted: (data) => {
      const { getExcipientLabel } = data || {};
      const { response, excipientLabel } = getExcipientLabel || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setValues(excipientLabel as ExcipientLabelPayload['excipientLabel']);
      }
    },
    onError: () => {
      setValues(null);
    },
  });

  const onExcipientLabelSubmit: SubmitHandler<AddExcipientLabelFormType> = async (data) => {
    const { name } = data || {};

    await updateExcipientLabel({
      variables: {
        updateExcipientLabelInput: {
          id: id || '',
          name,
        },
      },
    });
  };

  const fetchExcipientLabel = useCallback(async () => {
    id &&
      (await getExcipientLabel({
        variables: {
          getExcipientLabelInput: {
            id,
          },
        },
      }));
  }, [id, getExcipientLabel]);

  const setValues = (params: ExcipientLabelPayload['excipientLabel']) => {
    const { name } = params || {};
    setExcipientLabelValue('name', name || '');
  };

  useEffect(() => {
    id && fetchExcipientLabel();
  }, [id, fetchExcipientLabel]);

  const loading = ExcipientLoading || getLoading;

  return (
    <Fragment>
      <Box sx={flexCenterBetween}>
        <Typography variant="h5">{EDIT_EXCIPIENT_LABEL_TEXT}</Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item lg={6} md={12}>
          <FormProvider {...excipientLabelMethods}>
            <form onSubmit={handleExcipientLabelSubmit(onExcipientLabelSubmit)}>
              <ExcipientLabelForm loading={loading} isEdit />
            </form>
          </FormProvider>
        </Grid>
      </Grid>

      <BackdropLoader open={loading} text={EXCIPIENT_LABEL_IS_UPDATED_TEXT} />
    </Fragment>
  );
};

export default Edit;
